import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import type { Node, Edge } from 'reactflow';
import { api } from '../services/api';
import { FlowEditor } from '../components/FlowEditor/FlowEditor';
import type { FlowNodeData } from '../components/FlowEditor/types';
import { TemplateLibraryModal } from '../components/FlowEditor/TemplateLibraryModal';
import type { FlowTemplate } from '../components/FlowEditor/flowTemplates';
import { GitBranch, ArrowLeft, Save, AlertCircle, CheckCircle, Play, BookOpen, Star } from 'lucide-react';

const FlowEditorPage: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditMode = !!id;

  const [flowName, setFlowName] = useState('');
  const [flowDescription, setFlowDescription] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [isDefault, setIsDefault] = useState(false);
  const [nodes, setNodes] = useState<Node<FlowNodeData>[]>([]);
  const [edges, setEdges] = useState<Edge[]>([]);
  const [departments, setDepartments] = useState<any[]>([]);
  const [templates, setTemplates] = useState<any[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [isTemplateLibraryOpen, setIsTemplateLibraryOpen] = useState(false);
  const [isReadOnly, setIsReadOnly] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [showExitModal, setShowExitModal] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState<string | null>(null);

  useEffect(() => {
    loadDepartments();
    loadTemplates();

    if (isEditMode) {
      loadFlow();
    }
  }, [id]);

  // Detectar mudanças não salvas
  useEffect(() => {
    if (nodes.length > 0 || edges.length > 0) {
      setIsDirty(true);
    }
  }, [nodes, edges]);

  // Avisar ao fechar/recarregar a página
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isDirty && !isReadOnly) {
        e.preventDefault();
        e.returnValue = '';
        return '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isDirty, isReadOnly]);

  const loadDepartments = async () => {
    try {
      const deps = await api.getDepartments();
      setDepartments(deps);
    } catch (error) {
      console.error('Error loading departments:', error);
    }
  };

  const loadTemplates = async () => {
    try {
      const response = await api.getTemplates();
      const approvedTemplates = response.data.filter((t: any) => t.status === 'APPROVED');
      setTemplates(approvedTemplates);
    } catch (error) {
      console.error('Error loading templates:', error);
    }
  };

  const loadFlow = async () => {
    if (!id) return;

    try {
      setIsLoading(true);
      const response = await api.getFlow(id);
      const flow = response.flow;

      setFlowName(flow.name);
      setFlowDescription(flow.description || '');
      setIsActive(flow.isActive);
      setIsDefault(flow.isDefault);
      setIsReadOnly(flow.isDefault); // Fluxos default são read-only

      // Convert backend nodes to React Flow nodes
      const flowNodes: Node<FlowNodeData>[] = flow.nodes.map((node: any) => ({
        id: node.id,
        type: 'custom',
        position: JSON.parse(node.position || '{"x": 0, "y": 0}'),
        data: {
          label: node.label || '',
          nodeType: node.nodeType,
          config: JSON.parse(node.config || '{}'),
        },
      }));

      // Convert backend edges to React Flow edges
      const flowEdges: Edge[] = flow.edges.map((edge: any) => ({
        id: edge.id,
        source: edge.fromNodeId,
        target: edge.toNodeId,
        type: 'smoothstep',
        animated: true,
        label: edge.label || undefined,
        data: {
          condition: edge.condition ? JSON.parse(edge.condition) : null,
          priority: edge.priority || 0,
        },
      }));

      setNodes(flowNodes);
      setEdges(flowEdges);
    } catch (error: any) {
      console.error('Error loading flow:', error);
      setError(error.response?.data?.error || 'Erro ao carregar fluxo');
    } finally {
      setIsLoading(false);
    }
  };

  const validateFlow = (): boolean => {
    const errors: string[] = [];

    // Check if flow has a name
    if (!flowName.trim()) {
      errors.push('O fluxo precisa ter um nome');
    }

    // Check if flow has at least one START node
    const startNodes = nodes.filter(n => n.data.nodeType === 'START');
    if (startNodes.length === 0) {
      errors.push('O fluxo precisa ter pelo menos um nó START');
    }
    if (startNodes.length > 1) {
      errors.push('O fluxo deve ter apenas um nó START');
    }

    // Check if flow has at least one END or CLOSE_CONVERSATION node
    const endNodes = nodes.filter(n =>
      n.data.nodeType === 'END' || n.data.nodeType === 'CLOSE_CONVERSATION'
    );
    if (endNodes.length === 0) {
      errors.push('O fluxo precisa ter pelo menos um nó END ou CLOSE_CONVERSATION');
    }

    // Check for orphan nodes (nodes without connections)
    const connectedNodeIds = new Set<string>();
    edges.forEach(edge => {
      connectedNodeIds.add(edge.source);
      connectedNodeIds.add(edge.target);
    });

    const orphanNodes = nodes.filter(n =>
      n.data.nodeType !== 'START' &&
      n.data.nodeType !== 'END' &&
      !connectedNodeIds.has(n.id)
    );

    if (orphanNodes.length > 0) {
      errors.push(`${orphanNodes.length} nó(s) não conectado(s) encontrado(s)`);
    }

    // Validate node configurations
    nodes.forEach(node => {
      const nodeLabel = node.data.label || node.data.nodeType;

      switch (node.data.nodeType) {
        case 'SEND_MESSAGE':
          if (!node.data.config.message) {
            errors.push(`${nodeLabel}: Mensagem não configurada`);
          }
          break;
        case 'SEND_TEMPLATE':
          if (!node.data.config.templateName) {
            errors.push(`${nodeLabel}: Template não selecionado`);
          }
          break;
        case 'TRANSFER_DEPARTMENT':
          if (!node.data.config.departmentId) {
            errors.push(`${nodeLabel}: Departamento não selecionado`);
          }
          break;
        case 'UPDATE_STATUS':
          if (!node.data.config.status) {
            errors.push(`${nodeLabel}: Status não configurado`);
          }
          break;
        case 'SET_VARIABLE':
          if (!node.data.config.variableName) {
            errors.push(`${nodeLabel}: Nome da variável não configurado`);
          }
          break;
      }
    });

    setValidationErrors(errors);

    // Show feedback
    if (errors.length === 0) {
      setSuccess('✓ Fluxo válido! Nenhum erro encontrado.');
      setTimeout(() => setSuccess(null), 3000);
      setError(null);
    } else {
      setError(`Foram encontrados ${errors.length} erro(s) de validação:`);
    }

    return errors.length === 0;
  };

  const handleSave = async () => {
    setError(null);
    setSuccess(null);

    if (!validateFlow()) {
      setError('Corrija os erros de validação antes de salvar');
      return;
    }

    try {
      setIsSaving(true);

      // Map React Flow nodes to backend format
      const backendNodes = nodes.map((node) => ({
        tempId: node.id,
        nodeType: node.data.nodeType,
        config: node.data.config,
        position: node.position,
        label: node.data.label,
      }));

      // Map React Flow edges to backend format
      const backendEdges = edges.map(edge => ({
        fromNodeId: edge.source,
        toNodeId: edge.target,
        label: edge.label || null,
        condition: edge.data?.condition || null,
        priority: edge.data?.priority || 0,
      }));

      const payload = {
        name: flowName,
        description: flowDescription || null,
        templateName: null, // Template é definido dentro do fluxo via nó SEND_TEMPLATE
        isActive,
        isDefault,
        nodes: backendNodes,
        edges: backendEdges,
      };

      if (isEditMode) {
        await api.updateFlow(id!, payload);
        setSuccess('Fluxo atualizado com sucesso!');
        setIsDirty(false); // Limpar dirty flag após salvar
      } else {
        const response = await api.createFlow(payload);
        setSuccess('Fluxo criado com sucesso!');
        setIsDirty(false); // Limpar dirty flag após salvar

        // Redirect to edit mode with the new flow ID
        setTimeout(() => {
          navigate(`/flows/${response.flow.id}/edit`, { replace: true });
        }, 1500);
      }
    } catch (error: any) {
      console.error('Error saving flow:', error);
      setError(error.response?.data?.error || 'Erro ao salvar fluxo');
    } finally {
      setIsSaving(false);
    }
  };

  const handleNavigateBack = () => {
    if (isDirty && !isReadOnly) {
      setPendingNavigation('/flows');
      setShowExitModal(true);
    } else {
      navigate('/flows');
    }
  };

  const handleConfirmExit = () => {
    setShowExitModal(false);
    if (pendingNavigation) {
      navigate(pendingNavigation);
    }
  };

  const handleCancelExit = () => {
    setShowExitModal(false);
    setPendingNavigation(null);
  };

  const handleLoadTemplate = (template: FlowTemplate) => {
    try {
      const flowData = template.flow;

      // Load template data
      setFlowName(flowData.name);
      setFlowDescription(flowData.description);
      setIsActive(flowData.isActive);
      setIsDefault(flowData.isDefault);

      // Force update nodes and edges with key to trigger re-render
      setNodes([]);
      setEdges([]);

      setTimeout(() => {
        setNodes(flowData.nodes);
        setEdges(flowData.edges);

        setSuccess(`Template "${template.name}" carregado com sucesso! ${flowData.nodes.length} nós e ${flowData.edges.length} conexões.`);
        setTimeout(() => setSuccess(null), 5000);
      }, 100);

      setError(null);
      setValidationErrors([]);
    } catch (error: any) {
      console.error('Error loading template:', error);
      setError('Erro ao carregar template.');
      setTimeout(() => setError(null), 3000);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 p-3">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <button
              onClick={handleNavigateBack}
              className="text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200 flex-shrink-0"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>

            <GitBranch className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0" />

            <div className="flex-1 min-w-0">
              <input
                type="text"
                value={flowName}
                onChange={(e) => setFlowName(e.target.value)}
                disabled={isReadOnly}
                className="w-full text-lg font-bold border-0 border-b border-transparent hover:border-gray-300 focus:border-blue-500 focus:outline-none bg-transparent dark:text-white disabled:opacity-60 disabled:cursor-not-allowed"
                placeholder="Nome do Fluxo"
              />
            </div>
          </div>

          <div className="flex items-center gap-2 flex-shrink-0">
            {/* Toggle Ativo */}
            <div className="flex items-center gap-1.5">
              <span className="text-xs text-gray-600 dark:text-gray-400">Ativo:</span>
              <button
                type="button"
                onClick={() => !isReadOnly && setIsActive(!isActive)}
                disabled={isReadOnly}
                className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors disabled:opacity-60 disabled:cursor-not-allowed ${
                  isActive ? 'bg-green-600' : 'bg-gray-300 dark:bg-gray-600'
                }`}
              >
                <span
                  className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
                    isActive ? 'translate-x-5' : 'translate-x-0.5'
                  }`}
                />
              </button>
            </div>

            {/* Badge Padrão (read-only) */}
            {isDefault && (
              <div className="flex items-center gap-1.5 px-2 py-1 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-full">
                <Star className="w-3 h-3 text-yellow-600 dark:text-yellow-400 fill-yellow-600 dark:fill-yellow-400" />
                <span className="text-xs font-medium text-yellow-700 dark:text-yellow-400">Padrão</span>
              </div>
            )}

            <button
              onClick={() => validateFlow()}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              <Play className="w-3.5 h-3.5" />
              Validar
            </button>

            {!isReadOnly && (
              <button
                onClick={() => setIsTemplateLibraryOpen(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                <BookOpen className="w-3.5 h-3.5" />
                Templates
              </button>
            )}

            {!isReadOnly && (
              <button
                onClick={handleSave}
                disabled={isSaving}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                <Save className="w-3.5 h-3.5" />
                {isSaving ? 'Salvando...' : 'Salvar'}
              </button>
            )}
          </div>
        </div>

        {/* Read-Only Warning */}
        {isReadOnly && (
          <div className="mt-3 p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-semibold text-yellow-800 dark:text-yellow-300">
                Fluxo Padrão do Sistema (Somente Leitura)
              </p>
              <p className="text-sm text-yellow-700 dark:text-yellow-400 mt-1">
                Este é um fluxo padrão criado pelo sistema e não pode ser editado. Você pode visualizar sua estrutura, exportar ou duplicá-lo para criar uma versão editável.
              </p>
            </div>
          </div>
        )}

        {/* Alerts */}
        {error && (
          <div className="mt-3 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-start gap-2">
            <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
              {validationErrors.length > 0 && (
                <ul className="mt-2 text-sm text-red-600 dark:text-red-400 list-disc list-inside space-y-1">
                  {validationErrors.map((err, idx) => (
                    <li key={idx}>{err}</li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}

        {success && (
          <div className="mt-3 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg flex items-start gap-2">
            <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-green-600 dark:text-green-400">{success}</p>
          </div>
        )}
      </div>

      {/* Editor */}
      <div className="flex-1 overflow-hidden">
        <FlowEditor
          initialNodes={nodes}
          initialEdges={edges}
          departments={departments}
          templates={templates}
          onNodesChange={setNodes}
          onEdgesChange={setEdges}
          readOnly={isReadOnly}
        />
      </div>

      {/* Template Library Modal */}
      <TemplateLibraryModal
        isOpen={isTemplateLibraryOpen}
        onClose={() => setIsTemplateLibraryOpen(false)}
        onSelectTemplate={handleLoadTemplate}
      />

      {/* Exit Confirmation Modal */}
      {showExitModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Alterações não salvas
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Você tem alterações não salvas neste fluxo. Se sair agora, todas as mudanças serão perdidas.
            </p>
            <div className="flex gap-3">
              <button
                onClick={handleCancelExit}
                className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                Continuar Editando
              </button>
              <button
                onClick={handleConfirmExit}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                Sair sem Salvar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FlowEditorPage;
